import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios, { AxiosError } from 'axios';
import type { Game, GameItems, Filters, NewBalance, ChangePassword, ResetPassword, ErrorBanResponse, Player, PlayerLimits, TwoFactorAuth, FotoResponse } from '@/types';
import createQueryString from '@/utils/queryString';
import { getAuthFormHeaders, getAuthHeaders } from '@/utils/getHeaders';
import { storageDomain } from '@/utils/variables';
import { appendItems } from '@/utils/appendItems';
import { avatarAdapter } from '@/adapters/adapters';
import { useAuthStore } from './auth';
import { useGamesStore } from './games';
import { useGameBlocksStore } from './gameBlocks';

export const usePlayerStore = defineStore('player', () => {
    const authStore = useAuthStore();
    const loadingPlayerFlag = ref<boolean>(false);
    const player = ref<Player>({
        kyc_status: {
            foto: null
        },
        limits: null,
        is_2fa_enabled: false,
        id: null,
        address: {
            city: '',
            postal_code: '',
            address: '',
            country: ''
        },
        agent: null,
        avatar: {
            image: '',
            preview: ''
        },
        language_code: '',
        balance: 0,
        is_email_verified: false,
        birthday: null,
        block: 0,
        block_end: null,
        bonus_balance: 0,
        captcha: null,
        click_id: null,
        country_code: '',
        created_at: '',
        currency: '',
        doc1: null,
        doc1_uploaded_at: null,
        doc1_verified: 0,
        doc2: null,
        doc2_uploaded_at: null,
        doc2_verified: 0,
        doc3: null,
        doc3_uploaded_at: null,
        doc3_verified: 0,
        email: null,
        fs_count: 0,
        has_first_dep: false,
        is_streamer: false,
        last_activity: '',
        last_name: null,
        level: {
            id: 0,
            name: '',
            description: '',
            free_rounds_bonus: null,
            dep_bonus: null,
            next_level_amount: 0,
            logo: '',
            order: 0,
            flip: false,
            is_completed: false,
            current: false,
            is_locked: false,
            cost_level_amount: 0,
            cumulative_cost: 0
        },
        multiaccounts_count: 0,
        name: null,
        offer_id: '',
        partner_id: '',
        pin: null,
        processing: false,
        ref_info: null,
        sex: null,
        show_info: true,
        status_verification: null,
        sum_available_for_withdraw: 0,
        tel: null,
        time_limit: null,
        updated_at: null,
        username: '',
        wager_amount: 0,
        wagering: 0,
        unsubscribed: false,
        preferred_communication_channel: 'email'
    });
    const playerLimits = ref<PlayerLimits>({
        deposit_limit_value: 0,
        deposit_limit_usage: 0,
        deposit_limit_end_date: '',
        deposit_limit_start_date: '',
        wagering_limit_value: 0,
        wagering_limit_usage: 0,
        wagering_limit_end_date: '',
        wagering_limit_start_date: '',
        loss_limit_value: 0,
        loss_limit_usage: 0,
        loss_limit_end_date: '',
        loss_limit_start_date: ''
    });
    const favouriteGames = ref<GameItems>({
        items: [],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    function toToggleFavorite(game: Game, action: 'add' | 'delete'): void {
        if (action === 'add') {
            favouriteGames.value.items.push(game);
            favouriteGames.value.total++;
            favouriteGames.value.pages = favouriteGames.value.page;
        } else {
            favouriteGames.value.items = favouriteGames.value.items.filter((item) => item.id !== game.id);
            favouriteGames.value.total--;
        }
    }
    const isPlayer = computed(() => player.value.id !== null || !!localStorage.getItem('jwt'));
    const isPlayerHaveEmail = computed(() => !!player.value.email);
    function toUpdatePlayerField(field: string, value: string | number | boolean | null): void {
        player.value[field as keyof Player] = value as never;
    }
    function toUpdatePlayerKYCStatus(status: FotoResponse): void {
        player.value.kyc_status = status;
    }
    function toUpdatePlayerBalance(data: NewBalance): void {
        player.value.balance = data.balance;
        player.value.bonus_balance = data.bonus_balance;
        player.value.sum_available_for_withdraw = data?.sum_available_for_withdraw || player.value.sum_available_for_withdraw;
    }
    function toUpdateLimit(field: keyof PlayerLimits, value: string | number | boolean | null): void {
        playerLimits.value[field as keyof PlayerLimits] = value as never;
    }

    const awaitGetPlayer = async () => {
        const jwt = localStorage.getItem('jwt');
        if (!jwt) {
            return;
        }
        loadingPlayerFlag.value = true;
        try {
            const response = await axios.get<Player>('/main-api/users/me', getAuthHeaders());
            if (response.status === 200) {
                const rd = response.data;
                const data = {
                    ...rd,
                    avatar: avatarAdapter.mapAvatar(rd.avatar),
                    level: {
                        ...rd.level,
                        logo: `https://${storageDomain}/levels/${rd.level.logo}`
                    }
                };
                player.value = data;
            }
        } catch (error) {
            console.error(error);
            const err = error as AxiosError;
            if (err.response?.status === 403) {
                const response = err.response as ErrorBanResponse;
                authStore.toSetBanInfo(response.data.detail, true);
            }
            throw error;
        } finally {
            loadingPlayerFlag.value = false;
        }
    };
    const awaitUpdatePlayer = async (data: object): Promise<void> => {
        try {
            const response = await axios.put('/main-api/users/me', data, getAuthHeaders());
            if (response.status === 200) {
                const rd = response.data;
                const data = {
                    ...rd,
                    avatar: avatarAdapter.mapAvatar(rd.avatar),
                    level: {
                        ...player.value.level
                    }
                };
                player.value = data;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitChangePassword = async (data: ChangePassword): Promise<void> => {
        try {
            await axios.put('/main-api/users/me/password', data, getAuthHeaders());
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetEmailConfirmationToken = async (): Promise<void> => {
        try {
            await axios.post<{ email_verification_url: string }>('/main-api/users/me/email_verification_request', null, getAuthHeaders());
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitConfirmEmail = async (verification_token: string) => {
        try {
            await axios.post(`/main-api/users/me/verify_email?email_verification_token=${verification_token}`, null, getAuthHeaders());
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetResetPasswordToken = async (email: string): Promise<void> => {
        try {
            await axios.post('/main-api/users/reset_password_request', { email }, getAuthHeaders());
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitResetPassword = async (data: ResetPassword): Promise<void> => {
        try {
            await axios.post('/main-api/users/reset_password', data, getAuthHeaders());
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetFavouriteGames = async (filters?: Filters): Promise<void> => {
        const localFilters = filters || {
            page: 1,
            size: 50,
            is_favourite: true
        };
        const queryString = createQueryString(localFilters);
        try {
            const response = await axios.get<GameItems>(`/main-api/games/my/filter${queryString}`, getAuthHeaders());
            if (response.status === 200) {
                if (localFilters.page >= 2) {
                    appendItems(favouriteGames, response.data);
                } else {
                    favouriteGames.value = response.data;
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitToggleFavorite = async (game: Game): Promise<void> => {
        try {
            game.is_favourite = !game.is_favourite;
            const action = game.is_favourite ? 'add' : 'delete';
            const gamesStore = useGamesStore();
            const gameBlockStore = useGameBlocksStore();
            if (action === 'add') {
                await axios.post(`/main-api/games/favourites/${game.id}`, {}, getAuthHeaders());
            } else {
                await axios.delete(`/main-api/games/favourites/${game.id}`, getAuthHeaders());
            }
            toToggleFavorite(game, action);
            gamesStore.toUpdateGameFavoriteStatus(game, action);
            gameBlockStore.toUpdateGameBlockFavoriteStatus(game, action);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetPlayerLimits = async (): Promise<void> => {
        try {
            const response = await axios.get<PlayerLimits>('/main-api/users/me/limits', getAuthHeaders());
            if (response.status === 200) {
                playerLimits.value = response.data;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    async function toSetup2FA(): Promise<TwoFactorAuth> {
        try {
            const response = await axios.post<TwoFactorAuth>('/main-api/users/me/2fa/setup', null, getAuthHeaders());
            if (response.status === 200) {
                //player.value.is_2fa_enabled = true;
                return response.data;
            } else {
                throw new Error('failed_to_setup_2fa');
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
    async function toEnable2FA(code: string): Promise<void> {
        try {
            const response = await axios.post('/main-api/users/me/2fa/enable', { two_factor_code: code }, getAuthFormHeaders());
            if (response.status === 204) {
                player.value.is_2fa_enabled = true;
            } else {
                throw new Error('failed_to_enable_2fa');
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
    async function toDisable2FA(code: string): Promise<void> {
        try {
            const response = await axios.post('/main-api/users/me/2fa/disable', { two_factor_code: code }, getAuthFormHeaders());
            if (response.status === 204) {
                player.value.is_2fa_enabled = false;
            } else {
                throw new Error('failed_to_disable_2fa');
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    return {
        awaitGetEmailConfirmationToken,
        awaitConfirmEmail,
        awaitGetResetPasswordToken,
        awaitResetPassword,
        player,
        loadingPlayerFlag,
        awaitGetPlayer,
        awaitChangePassword,
        toUpdatePlayerBalance,
        isPlayer,
        isPlayerHaveEmail,
        awaitUpdatePlayer,
        toUpdatePlayerField,
        favouriteGames,
        awaitGetFavouriteGames,
        awaitToggleFavorite,
        awaitGetPlayerLimits,
        playerLimits,
        toUpdateLimit,
        toSetup2FA,
        toEnable2FA,
        toDisable2FA,
        toUpdatePlayerKYCStatus
    };
});
